import type { CSSProperties } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, type NavigateFunction } from 'react-router-dom';
import { settings } from './store';

/** Name shared by the clicked element and its counterpart on the next page. */
const VT_NAME = 'mode-pick';

/**
 * How a page was reached: from a start-menu mode, a variant card, a page shortcut,
 * the mini board on the Play page (into the game board) or the sidebar.
 */
export type MorphTarget = 'mode' | 'preset' | 'page' | 'board' | 'nav';
interface MorphState { vt: true; target: MorphTarget }

type DocWithVT = Document & { startViewTransition?: (cb: () => void) => { finished: Promise<void> } };

function motionAllowed(): boolean {
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  return !!(document as DocWithVT).startViewTransition && !reduced && settings.get().animations;
}

/**
 * Run a DOM update as a View Transition (cross-fade plus any named elements morphing).
 * The browser only starts a transition on its next rendered frame (never, in a hidden tab),
 * so the update runs directly if the transition has not started shortly after.
 */
export function withTransition(update: () => void, named?: HTMLElement | null) {
  if (!motionAllowed()) { update(); return; }
  if (named) named.style.setProperty('view-transition-name', VT_NAME);
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    flushSync(update);
  };
  const fallback = setTimeout(() => { named?.style.removeProperty('view-transition-name'); run(); }, 400);
  const t = (document as DocWithVT).startViewTransition!(() => { clearTimeout(fallback); run(); });
  t.finished.catch(() => {}).finally(() => named?.style.removeProperty('view-transition-name'));
}

/** Navigate so that `from` flies into its place on the next page while the rest cross-fades. */
export function navigateMorph(nav: NavigateFunction, to: string, from: HTMLElement | null, target: MorphTarget) {
  const state: MorphState = { vt: true, target };
  withTransition(() => nav(to, { state }), from);
}

/** How the current page was reached, if through one of the animated paths above. */
export function useArrival(): MorphTarget | null {
  const s = useLocation().state as Partial<MorphState> | null;
  return s?.vt ? s.target ?? 'page' : null;
}

/** Style + class for the element that receives the morph on the destination page. */
export function morphTarget(active: boolean): { style?: CSSProperties; className: string } {
  return active ? { style: { viewTransitionName: VT_NAME } as CSSProperties, className: 'vt-target' } : { className: '' };
}

/** True for plain left clicks (not new-tab / new-window clicks), which we may animate. */
export const isPlainClick = (e: React.MouseEvent) => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
